// The personal schedule solver.
//
// Greedy selection over overlapping intervals with travel costs gives visibly
// wrong answers and users notice, so each day is a shortest-path problem over a
// time-expanded graph instead.
//
//   Breakpoints  the sorted union of every set start and end on the day, plus the
//                bounds of any required rest window. No fixed discretization, so
//                the state space stays small without losing precision.
//   State        (breakpoint index, current stage)
//   Transitions  stay at this stage through the next interval and accrue whatever
//                plays there, or leave for another stage and accrue nothing until
//                arrival, per the walk matrix.
//
// Complexity is O(T * S^2) with T near 120 and S near 7, which is single-digit
// milliseconds in a browser. That is the point: the schedule re-solves live while
// a slider moves.
//
// Hard locks prune states rather than penalizing them. If pruning empties the
// state space the locks are mutually infeasible, and that is reported rather than
// silently relaxed, because a solver that quietly drops a lock is worse than one
// that says it cannot.

import type { TuningConfig } from './config.ts';
import { DEFAULT_CONFIG } from './config.ts';
import { attendanceMultiplier, intervalAccrual } from './partial.ts';
import { walkMs } from './walk.ts';
import type { DayContext, FestivalSet, Id, Plan, PlanBlock, Stage } from './types.ts';
import { dayBounds } from './time.ts';

export interface RestRequirement {
  id: Id;
  stageId: Id;
  startTs: number;
  endTs: number;
  label: string;
  /** Utility for the whole window, spread evenly across it. */
  utility: number;
}

export interface SolveInput {
  ctx: DayContext;
  /** u_full per set id. */
  utility: Map<Id, number>;
  hardLockSetIds: Id[];
  rest: RestRequirement[];
  /** Where the day starts. Camp, normally. */
  homeStageId?: Id;
  cfg?: TuningConfig;
}

export interface SolveResult {
  blocks: PlanBlock[];
  totalUtility: number;
  infeasible?: { lockedSetIds: Id[]; reason: string };
  /** Sets the attendance floor dropped between passes, for the interface to explain. */
  droppedForFloor: Id[];
  /** Rest windows a hard lock took priority over. Reported, never absorbed. */
  restOverridden: Id[];
  breakpointCount: number;
}

/** How the solver reached a state, which is what backtracking replays. */
interface Step {
  fromStage: number;
  fromIndex: number;
  /** Set when the step was a walk: the moment the destination was reached. */
  arrival?: number;
}

/** A stretch of the day spent somewhere. `stage` is -1 while walking. */
export interface Segment {
  startTs: number;
  endTs: number;
  stage: number;
}

const NEG = -Infinity;

export function solveDay(input: SolveInput): SolveResult {
  const cfg = input.cfg ?? DEFAULT_CONFIG;
  const { ctx } = input;
  const bounds = dayBounds(ctx.day);

  const stages = ctx.stages;
  const stageIndex = new Map<Id, number>();
  stages.forEach((s, i) => stageIndex.set(s.id, i));
  const S = stages.length;

  const sets = ctx.sets
    .filter((s) => s.dayId === ctx.day.id && s.endTs > s.startTs)
    .filter((s) => stageIndex.has(s.stageId));

  const marks = new Set<number>([bounds.start, bounds.end]);
  for (const s of sets) {
    marks.add(clamp(s.startTs, bounds));
    marks.add(clamp(s.endTs, bounds));
  }
  for (const r of input.rest) {
    marks.add(clamp(r.startTs, bounds));
    marks.add(clamp(r.endTs, bounds));
  }
  const bp = [...marks].sort((a, b) => a - b);
  const N = bp.length;
  if (N < 2) {
    return { blocks: [], totalUtility: 0, droppedForFloor: [], restOverridden: [], breakpointCount: N };
  }

  // What plays where, per interval. Breakpoints include every set boundary, so a
  // set either covers an interval completely or not at all.
  const setAt: (string | null)[][] = [];
  for (let i = 0; i < N - 1; i++) {
    const row: (string | null)[] = new Array(S).fill(null);
    for (const s of sets) {
      if (s.startTs <= bp[i] && s.endTs >= bp[i + 1]) row[stageIndex.get(s.stageId)!] = s.id;
    }
    setAt.push(row);
  }

  // Constraints. `null` means unconstrained; an index means the solver must be at
  // that stage for the whole interval and may not be in transit through it.
  const pinned: (number | null)[] = new Array(N - 1).fill(null);
  const lockedSets = sets.filter((s) => input.hardLockSetIds.includes(s.id));
  const collisions: Id[] = [];

  for (const s of lockedSets) {
    const si = stageIndex.get(s.stageId)!;
    for (let i = 0; i < N - 1; i++) {
      if (bp[i] >= s.startTs && bp[i + 1] <= s.endTs) {
        if (pinned[i] !== null && pinned[i] !== si) collisions.push(s.id);
        pinned[i] = si;
      }
    }
  }
  if (collisions.length > 0) {
    return {
      blocks: [],
      totalUtility: 0,
      infeasible: {
        lockedSetIds: [...new Set([...collisions, ...lockedSets.map((s) => s.id)])],
        reason: 'Two locked sets overlap in time at different stages.',
      },
      droppedForFloor: [],
      restOverridden: [],
      breakpointCount: N,
    };
  }

  // A rest window under a hard lock loses, because the lock is the user's own
  // decision. Which windows lost is reported rather than absorbed: a plan that
  // quietly attends a locked set through a required camp break looks like a
  // plan that satisfied both.
  const restOverridden: Id[] = [];
  for (const r of input.rest) {
    const si = stageIndex.get(r.stageId);
    if (si === undefined) continue;
    let lost = false;
    for (let i = 0; i < N - 1; i++) {
      if (bp[i] < r.startTs || bp[i + 1] > r.endTs) continue;
      if (pinned[i] === null) pinned[i] = si;
      else if (pinned[i] !== si) lost = true;
    }
    if (lost) restOverridden.push(r.id);
  }

  const restPerMs = new Map<number, number>();
  for (const r of input.rest) {
    const si = stageIndex.get(r.stageId);
    if (si === undefined || r.endTs <= r.startTs) continue;
    restPerMs.set(si, (restPerMs.get(si) ?? 0) + r.utility / (r.endTs - r.startTs));
  }

  const setById = new Map(sets.map((s) => [s.id, s]));

  const accrue = (si: number, from: number, to: number, i: number, util: Map<Id, number>): number => {
    if (to <= from || i < 0 || i >= N - 1) return 0;
    const id = setAt[i][si];
    if (id) {
      const s = setById.get(id)!;
      return (util.get(id) ?? 0) * intervalAccrual(s.startTs, s.endTs, from, to, cfg);
    }
    const rest = restPerMs.get(si);
    if (rest && input.rest.some((r) => stageIndex.get(r.stageId) === si && from >= r.startTs && to <= r.endTs)) {
      return rest * (to - from);
    }
    return 0;
  };

  const runDp = (util: Map<Id, number>) => {
    const value: number[][] = Array.from({ length: N }, () => new Array(S).fill(NEG));
    const back: (Step | null)[][] = Array.from({ length: N }, () => new Array(S).fill(null));

    const home = input.homeStageId ? stageIndex.get(input.homeStageId) : undefined;
    if (home !== undefined) value[0][home] = 0;
    else for (let s = 0; s < S; s++) value[0][s] = 0;

    for (let i = 0; i < N - 1; i++) {
      for (let s = 0; s < S; s++) {
        if (value[i][s] === NEG) continue;
        const pin = pinned[i];

        if (pin === null || pin === s) {
          const v = value[i][s] + accrue(s, bp[i], bp[i + 1], i, util);
          if (v > value[i + 1][s]) {
            value[i + 1][s] = v;
            back[i + 1][s] = { fromStage: s, fromIndex: i };
          }
        }

        if (pin !== null) continue;
        for (let d = 0; d < S; d++) {
          if (d === s) continue;
          const arrival = bp[i] + walkMs(ctx.walk, stages[s].id, stages[d].id);
          if (arrival >= bp[N - 1]) continue;
          let j = i + 1;
          while (j < N && bp[j] < arrival) j++;
          if (j >= N) continue;
          let blocked = false;
          for (let k = i; k < j; k++) if (pinned[k] !== null) { blocked = true; break; }
          if (blocked) continue;
          const v = value[i][s] + accrue(d, arrival, bp[j], j - 1, util);
          if (v > value[j][d]) {
            value[j][d] = v;
            back[j][d] = { fromStage: s, fromIndex: i, arrival };
          }
        }
      }
    }
    return { value, back };
  };

  // Solve, then drop any set the attendance floor says is not worth the walk at the
  // fraction this path gives it, and solve again. The additive accrual the DP uses
  // cannot see the gate, so this is where the gate gets enforced.
  let util = new Map(input.utility);
  const dropped: Id[] = [];
  let segments: Segment[] | null = null;

  for (let pass = 0; pass < 6; pass++) {
    const dp = runDp(util);
    segments = backtrack(dp.value, dp.back, bp, N, S);
    if (!segments) break;
    const blocks = segmentsToBlocks(segments, stages, sets, input.rest, util, cfg);
    const under = blocks
      .filter((b) => b.setId)
      .filter((b) => {
        const s = setById.get(b.setId!)!;
        return attendanceMultiplier(s.startTs, s.endTs, b.startTs, b.endTs, cfg) < 0.02;
      })
      .map((b) => b.setId!)
      .filter((id) => (util.get(id) ?? 0) > 0);
    if (under.length === 0) break;
    util = new Map(util);
    for (const id of under) {
      util.set(id, 0);
      dropped.push(id);
    }
  }

  if (!segments) {
    return {
      blocks: [],
      totalUtility: 0,
      infeasible: {
        lockedSetIds: input.hardLockSetIds,
        reason: 'No route satisfies every lock and rest window once walk time is counted.',
      },
      droppedForFloor: dropped,
      restOverridden,
      breakpointCount: N,
    };
  }

  // A set the floor rejected is not attended. The walker may still be standing at
  // that stage waiting for the next thing, so the span becomes travel rather than a
  // one-minute set nobody is watching.
  const blocks = segmentsToBlocks(segments, stages, sets, input.rest, input.utility, cfg).map((b) => {
    if (!b.setId) return b;
    const s = setById.get(b.setId);
    if (!s || attendanceMultiplier(s.startTs, s.endTs, b.startTs, b.endTs, cfg) >= 0.02) return b;
    return {
      stageId: b.stageId,
      startTs: b.startTs,
      endTs: b.endTs,
      mode: 'travel' as const,
      locked: false,
      utility: 0,
    };
  });
  for (const b of blocks) if (b.setId && input.hardLockSetIds.includes(b.setId)) b.locked = true;
  return {
    blocks,
    totalUtility: blocks.reduce((a, b) => a + b.utility, 0),
    droppedForFloor: dropped,
    restOverridden,
    breakpointCount: N,
  };
}

function clamp(ts: number, bounds: { start: number; end: number }): number {
  return Math.max(bounds.start, Math.min(bounds.end, ts));
}

function backtrack(
  value: number[][],
  back: (Step | null)[][],
  bp: number[],
  N: number,
  S: number,
): Segment[] | null {
  let bestStage = -1;
  let bestValue = NEG;
  for (let s = 0; s < S; s++) {
    if (value[N - 1][s] > bestValue) {
      bestValue = value[N - 1][s];
      bestStage = s;
    }
  }
  if (bestStage < 0 || bestValue === NEG) return null;

  const rev: Segment[] = [];
  let i = N - 1;
  let s = bestStage;
  let guard = 0;
  while (i > 0 && guard++ < N * S * 4) {
    const step = back[i][s];
    if (!step) return null;
    if (step.arrival === undefined) {
      rev.push({ startTs: bp[step.fromIndex], endTs: bp[i], stage: s });
    } else {
      rev.push({ startTs: step.arrival, endTs: bp[i], stage: s });
      rev.push({ startTs: bp[step.fromIndex], endTs: step.arrival, stage: -1 });
    }
    i = step.fromIndex;
    s = step.fromStage;
  }
  return rev.reverse();
}

function segmentsToBlocks(
  segments: Segment[],
  stages: Stage[],
  sets: FestivalSet[],
  rest: RestRequirement[],
  util: Map<Id, number>,
  cfg: TuningConfig,
): PlanBlock[] {
  const raw: PlanBlock[] = [];

  for (const seg of segments) {
    if (seg.endTs <= seg.startTs) continue;
    if (seg.stage < 0) {
      raw.push({ stageId: '', startTs: seg.startTs, endTs: seg.endTs, mode: 'travel', locked: false, utility: 0 });
      continue;
    }
    const stageId = stages[seg.stage].id;
    const here = sets.filter((s) => s.stageId === stageId && s.startTs < seg.endTs && s.endTs > seg.startTs);
    if (here.length === 0) {
      const r = rest.find((x) => x.stageId === stageId && seg.startTs >= x.startTs && seg.endTs <= x.endTs);
      if (r) {
        const share = (seg.endTs - seg.startTs) / Math.max(1, r.endTs - r.startTs);
        raw.push({
          constraintId: r.id,
          stageId,
          startTs: seg.startTs,
          endTs: seg.endTs,
          mode: 'rest',
          locked: true,
          utility: r.utility * share,
        });
      }
      continue;
    }
    for (const s of here) {
      const start = Math.max(seg.startTs, s.startTs);
      const end = Math.min(seg.endTs, s.endTs);
      if (end <= start) continue;
      raw.push({
        setId: s.id,
        stageId,
        startTs: start,
        endTs: end,
        mode: 'full',
        locked: false,
        utility: 0,
      });
    }
  }

  // Adjacent segments at one stage split a set in two. Merge those back before
  // scoring, so the curve is applied once to the real span rather than twice to
  // two halves, which is not the same number.
  const merged: PlanBlock[] = [];
  for (const b of raw.sort((a, b2) => a.startTs - b2.startTs)) {
    const prev = merged[merged.length - 1];
    if (prev && prev.endTs === b.startTs && prev.setId && prev.setId === b.setId) {
      prev.endTs = b.endTs;
      continue;
    }
    if (prev && prev.endTs === b.startTs && !prev.setId && !b.setId && prev.mode === b.mode && prev.stageId === b.stageId) {
      prev.endTs = b.endTs;
      continue;
    }
    merged.push({ ...b });
  }

  for (const b of merged) {
    if (!b.setId) continue;
    const s = sets.find((x) => x.id === b.setId)!;
    b.utility = (util.get(s.id) ?? 0) * attendanceMultiplier(s.startTs, s.endTs, b.startTs, b.endTs, cfg);
    const full = b.startTs <= s.startTs && b.endTs >= s.endTs;
    b.mode = full ? 'full' : b.endTs >= s.endTs ? 'catch_close' : 'catch_open';
  }
  return merged;
}

export function planFromSolve(
  ownerKind: 'user' | 'workspace',
  ownerId: Id,
  dayId: Id,
  result: SolveResult,
): Plan {
  return {
    id: `${ownerKind}:${ownerId}:${dayId}`,
    ownerKind,
    ownerId,
    dayId,
    blocks: result.blocks,
    totalUtility: result.totalUtility,
    solvedAt: new Date().toISOString(),
    stale: false,
    infeasible: result.infeasible,
  };
}
